/**
 * This module can be used to map an "action id" back to a promise resolution. It is useful when a query/response
 * mechanism needs to know when a dispatch action has completed.
 */

import Logger from "../../clients/logger";
import { ActionId, ActionResult } from "./types";

// maps actionId to Promise
export let actionPromiseMap: Record<string, Function> = {};

// maps actionId to action results
export let actionResultMap: Record<string, ActionResult> = {};

// counter used to generate actionUniqueIDs
let actionUniqueIDCounter = 0;

/**
 * Create an action Identifier with unique id and type
 *
 * @param {string} type the type of the action (e.g. InteropRaiseIntent["type"])
 * @returns {ActionId}
 */
export const actionIdentifier = (type: string): ActionId => {
	const actionId: ActionId = {
		actionUniqueID: `actionId-${type}-${++actionUniqueIDCounter}`,
		type,
	};
	return actionId;
};

/**
 * Returns promise for an action result -- dovetails with setActionResult()
 *
 * NOTE: the use case justifies supporting only one client per actionId; if multiple clients were needed then need an array of promises
 *
 * @param {ActionId} actionId the action id that was input to the action dispatch()
 * @returns {Promise<ActionResult>}
 */
export const actionResult = (actionId: ActionId): Promise<ActionResult> => {
	const actionResultPromise = (resolve) => {
		// if result is already available then immediately resolve the promise
		if (actionResultMap[actionId.actionUniqueID]) {
			Logger.debug("InteropService: actionResult resolved", actionId, actionResultMap[actionId.actionUniqueID]);
			resolve(actionResultMap[actionId.actionUniqueID]);
			delete actionResultMap[actionId.actionUniqueID];
		} else {
			// save the promise so it can be resolved when result is set
			actionPromiseMap[actionId.actionUniqueID] = resolve;
		}
	};

	return new Promise(actionResultPromise);
};

/**
 * Sets action result and resolves any pending promise waiting on the result -- dovetails with actionResult()
 *
 * NOTE: the use case justifies supporting only one client per actionId; if multiple clients were needed then need an array of promises
 *
 * @param {ActionId} actionId identifies the result being set
 * @param {ActionResult} aResult the result being set; actionId.type must match aResult.type (if used property it will)
 */
export const setActionResult = (actionId: ActionId, aResult: ActionResult) => {
	if (actionId.type !== aResult.type) {
		Logger.error("InteropService: setActionResult type mismatch", actionId, aResult);
	}
	const promiseResolveForAction = actionPromiseMap[actionId.actionUniqueID];
	// if promise is waiting on the result, then resolve it
	if (promiseResolveForAction) {
		Logger.debug("InteropService: setActionResult resolved", actionId, aResult);
		promiseResolveForAction(aResult);
		delete actionPromiseMap[actionId.actionUniqueID];
	} else {
		// else save the result for when it is requested by actionResult
		actionResultMap[actionId.actionUniqueID] = aResult;
	}
};
